import React, { useEffect, useRef, useState } from 'react';
import { signOut, getCurrentUser, fetchUserAttributes, fetchAuthSession } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { useNavigate, useLocation } from 'react-router-dom';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Line, Bar } from 'react-chartjs-2';
import 'chart.js/auto';
import { Elements, CardElement, useStripe, useElements, AddressElement } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import '../styles.css';

const stripePromise = loadStripe('pk_live_51PRDa3ITs3jl5ALXZDPxAs6NGLeTknOD9ntsor1UlC9b2eQ4vVqs7bSA022qIJk6MPzDxQaaRdp5zzpSwn9JR0ng00B0891FQh');

function Dashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [unfilteredCompsData, setUnfilteredCompsData] = useState([]);
  const [rawCompsData, setRawCompsData] = useState([]);
  const [filteredCompsData, setFilteredCompsData] = useState([]);
  const [avmData, setAvmData] = useState(null);
  const [subjectPropertyInfo, setSubjectPropertyInfo] = useState(null);
  const [infoModal, setInfoModal] = useState(null); // Clickable (i) info modal for mobile & desktop
  const [chartData, setChartData] = useState({ labels: [], avgPrices: [], salesVolume: [] });
  const [hasAnalyzed, setHasAnalyzed] = useState(false); // Tracks if analyze was run
  const [inputsDirty, setInputsDirty] = useState(false); // Re-enables Analyze if inputs changed
  const [error, setError] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [credits, setCredits] = useState(0);
  const [userId, setUserId] = useState(null);
  const [timeframe, setTimeframe] = useState('Last Year');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Freemium state
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [hasUnlockedReport, setHasUnlockedReport] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showAppealModal, setShowAppealModal] = useState(false);
  const [showAddressGuideModal, setShowAddressGuideModal] = useState(false);
  const [isUnlocking, setIsUnlocking] = useState(false);
  const [previewEstimatedValue, setPreviewEstimatedValue] = useState(null);
  const [cachedSubjectId, setCachedSubjectId] = useState(null);
  const [cachedSubjectProperty, setCachedSubjectProperty] = useState(null);
  const [cachedDetailedProperty, setCachedDetailedProperty] = useState(null);
  const [cachedFilters, setCachedFilters] = useState(null);
  const [assessedValue, setAssessedValue] = useState('');
  const [previewComps, setPreviewComps] = useState([]);
  const [hasUsedFreeSearch, setHasUsedFreeSearch] = useState(() => {
    return localStorage.getItem('casalytix_free_search_used') === 'true';
  });
  const addressInputRef = useRef(null);
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const autocompleteServiceRef = useRef(null);
  const suggestionsRef = useRef(null);

  const BASE_API_URL = 'https://h1n5dryzeg.execute-api.us-east-1.amazonaws.com/prod';
  const REALESTATEAPI_KEY = process.env.REACT_APP_REALESTATEAPI_KEY || 'CASALYTIX-947c-7c5a-b690-f71f8c56696a';

  // Helper function to convert string to title case
  const toTitleCase = (str) => {
    if (!str || str === 'N/A') return str;
    return str.toLowerCase().replace(/(^|\s)\w/g, (letter) => letter.toUpperCase());
  };



  useEffect(() => {
    let isMounted = true;

    const checkAuthAndFetch = async () => {
      try {
        console.log('Checking authentication state...');
        const user = await getCurrentUser();
        if (isMounted && user) {
          console.log('User authenticated, fetching details...');
          const attributes = await fetchUserAttributes();
          const email = attributes.email;
          console.log('User fetched, email:', email);
          setUserId(email);
          setIsAuthenticated(true);
          await fetchCredits(email);
        }
      } catch (err) {
        console.error('Error in checkAuthAndFetch:', err);
        if (isMounted) {
          setError('Please sign in to continue.');
          navigate('/login', { replace: true });
        }
      }
    };

    const authListener = Hub.listen('auth', ({ payload: { event } }) => {
      console.log('Auth event:', event);
      if (event === 'signedIn') {
        checkAuthAndFetch();
      } else if (event === 'signedOut') {
        if (isMounted) {
          setIsAuthenticated(false);
          setCredits(0);
          navigate('/login', { replace: true });
        }
      }
    });

    checkAuthAndFetch();

    return () => {
      isMounted = false;
      authListener();
    };
  }, [navigate]);

  useEffect(() => {
    const fetchCreditsWithRetry = async (userId, retries = 3, delay = 2000) => {
      if (!userId) return;
      for (let i = 0; i < retries; i++) {
        try {
          const creditsValue = await fetchCredits(userId);
          if (creditsValue >= 0) {
            setCredits(creditsValue);
            return;
          }
        } catch (err) {
          console.error(`Fetch credits attempt ${i + 1} failed:`, err);
        }
        if (i < retries - 1) {
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
      setError('Failed to load credits after retries.');
    };

    if (userId) {
      if (location.state?.credits > 0) {
        setCredits(location.state.credits);
      } else if (location.state?.fromPayment || location.state?.fromSignup || credits === 0) {
        console.log('Triggering credits fetch due to payment, signup w/payment or zero credits...');
        fetchCreditsWithRetry(userId);
      }
    }
  }, [userId, location.state]);



  const fetchWithTimeout = async (url, options, timeout = 10000) => {
    const controller = new AbortController();
    const id = setTimeout(() => controller.abort(), timeout);
    try {
      console.log('Fetching URL:', url);
      const response = await fetch(url, { ...options, signal: controller.signal });
      console.log('Fetch response status:', response.status);
      clearTimeout(id);
      return response;
    } catch (err) {
      clearTimeout(id);
      console.error('Fetch error:', err.message);
      if (err.name === 'AbortError') {
        throw new Error('Request timed out');
      }
      throw err;
    }
  };

  const fetchCredits = async (userIdParam) => {
    if (!userIdParam) {
      console.log('No userId provided, skipping fetchCredits');
      return -1;
    }
    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();
      if (!idToken) {
        throw new Error('No ID token available');
      }
      console.log('ID token retrieved for fetchCredits');

      const url = `${BASE_API_URL}/user-credits?userId=${encodeURIComponent(userIdParam)}&idToken=${encodeURIComponent(idToken)}&_=${Date.now()}`;
      console.log('fetchCredits URL:', url);
      const response = await fetchWithTimeout(url, {}, 10000);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('fetchCredits failed:', response.status, errorText);
        throw new Error(`Failed to fetch credits: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      console.log('fetchCredits response data:', data);
      return data.credits || 0;
    } catch (err) {
      console.error('fetchCredits error:', err.message);
      throw err;
    }
  };

  const deductCredit = async () => {
    if (!userId) return;
    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();
      console.log('deductCredit initiated for userId:', userId);
      const url = `${BASE_API_URL}/deduct-credit?idToken=${encodeURIComponent(idToken)}`;
      const response = await fetchWithTimeout(url, {
        method: 'POST',
        body: JSON.stringify({ userId, creditsToDeduct: 1 })
      }, 10000);
      if (!response.ok) {
        const errorText = await response.text();
        console.error('deductCredit failed:', response.status, errorText);
        throw new Error(`Failed to deduct credit: ${response.status} - ${errorText}`);
      }
      const data = await response.json();
      console.log('deductCredit response data:', data);
      setCredits(data.credits || 0);
    } catch (err) {
      console.error('deductCredit error:', err.message);
      setError('Failed to deduct credit: ' + err.message);
    }
  };

  // Helper to extract clean address string from any API item format
  const extractAddressString = (item) => {
    if (!item) return '';
    if (typeof item === 'string') return item;
    if (typeof item.address === 'string' && item.address.trim()) return item.address;
    if (item.full_address && typeof item.full_address === 'string') return item.full_address;
    if (item.formattedAddress && typeof item.formattedAddress === 'string') return item.formattedAddress;
    if (item.formatted_address && typeof item.formatted_address === 'string') return item.formatted_address;
    if (item.description && typeof item.description === 'string') return item.description;
    if (item.label && typeof item.label === 'string') return item.label;

    if (item.address && typeof item.address === 'object') {
      const a = item.address;
      const parts = [a.street || a.line1 || a.addressLine1, a.city, a.state, a.zip || a.postal_code].filter(Boolean);
      if (parts.length > 0) return parts.join(', ');
    }

    const parts = [item.street || item.line1, item.city, item.state, item.zip || item.postal_code].filter(Boolean);
    if (parts.length > 0) return parts.join(', ');

    return typeof item === 'object' ? (Object.values(item).filter(v => typeof v === 'string').join(', ') || JSON.stringify(item)) : String(item);
  };

  // RealEstateAPI Autocomplete handler via backend Lambda proxy
  const autocompleteDebounceRef = useRef(null);

  const handleAddressInputChange = (e) => {
    const val = e.target.value;
    setSelectedAddress(val ? { address: val } : null);
    setInputsDirty(true);

    if (!val || val.length < 3) {
      setAddressSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    if (autocompleteDebounceRef.current) {
      clearTimeout(autocompleteDebounceRef.current);
    }

    autocompleteDebounceRef.current = setTimeout(async () => {
      try {
        // Try proxy endpoint first
        let response = await fetch(`${BASE_API_URL}/autocomplete`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ search: val })
        });

        // Fallback to direct RealEstateAPI if proxy is unavailable
        if (!response.ok) {
          response = await fetch('https://api.realestateapi.com/v2/autocomplete', {
            method: 'POST',
            headers: {
              'x-api-key': REALESTATEAPI_KEY,
              'Content-Type': 'application/json',
              'Accept': 'application/json',
            },
            body: JSON.stringify({ search: val })
          });
        }

        if (response.ok) {
          const resData = await response.json();
          const list = resData.data || resData.result || resData.suggestions || resData.autocomplete || resData || [];
          if (Array.isArray(list) && list.length > 0) {
            setAddressSuggestions(list.map(item => ({
              address: extractAddressString(item),
              raw: item
            })).filter(s => s.address && s.address.length > 3));
            setShowSuggestions(true);
          } else {
            setAddressSuggestions([]);
            setShowSuggestions(false);
          }
        }
      } catch (err) {
        console.error('Autocomplete fetch error:', err);
      }
    }, 300);
  };

  const handleSuggestionSelect = (suggestion) => {
    const addr = typeof suggestion === 'string' ? suggestion : (suggestion.address || extractAddressString(suggestion));
    setSelectedAddress({ address: addr });
    setInputsDirty(true);
    setAddressSuggestions([]);
    setShowSuggestions(false);
    if (addressInputRef.current) addressInputRef.current.blur();
  };

  const handleSignOut = async () => {
    try {
      console.log('Initiating sign out...');
      await signOut();
      setIsAuthenticated(false);
      setCredits(0);
      console.log('Sign out successful, navigating to login');
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Sign out error:', error.message);
      setError('Sign out failed: ' + error.message);
    }
  };

  // ===== PHASE 1: FREE PREVIEW (1 API credit via v3/PropertyComps) =====
  const previewProperty = async () => {
    if (!selectedAddress) {
      alert('Please enter your property address.');
      return;
    }

    // Enforce free search limit (1 free search per user)
    if (hasUsedFreeSearch && credits <= 0) {
      setShowPaymentModal(true);
      return;
    }

    setError(null);
    setDataLoaded(false);
    setIsAnalyzing(true);
    setIsPreviewMode(false);
    setHasUnlockedReport(false);
    setFilteredCompsData([]);
    setRawCompsData([]);
    setUnfilteredCompsData([]);
    setPreviewComps([]);
    setChartData({ labels: [], avgPrices: [], salesVolume: [] });

    const filters = {
      timeframe: timeframe === 'Leave Blank' ? 'Last Year' : timeframe,
      radius: document.getElementById('radius').value.replace(' miles', '') || '5',
      propertyType: (() => {
        const raw = document.getElementById('property-type').value;
        if (raw === 'Leave Blank') return 'Single Family Residence';
        if (raw === 'Multi Family') return 'Multi Family Residence';
        return raw;
      })(),
    };

    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();

      // Single API call: PropertyComps returns BOTH the subject property details AND 1 teaser comp (1 credit)
      const previewPayload = {
        address: selectedAddress.address,
        max_radius_miles: parseInt(filters.radius) || 5,
        max_days_back: 365,
        max_results: 1,
        arms_length: true,
      };

      const compsUrl = `${BASE_API_URL}/propertyComps?idToken=${encodeURIComponent(idToken)}`;
      const compsResponse = await fetchWithTimeout(compsUrl, {
        method: 'POST',
        body: JSON.stringify(previewPayload)
      }, 15000);

      if (!compsResponse.ok) throw new Error(`Property lookup failed: ${compsResponse.status}`);
      const compsData = await compsResponse.json();
      console.log('Preview PropertyComps response:', compsData);

      const subject = compsData.subject;
      if (!subject) throw new Error('Subject property not found.');

      // Extract subject property details with robust fallbacks across all RealEstateAPI v3 schema fields
      const estimatedValue = parseFloat(subject.estimatedValue) || parseFloat(subject.avm?.price) || 750000;
      const propertyLivingSqFt = Number(subject.propertyInfo?.livingSquareFeet) || Number(subject.building?.size) || Number(subject.structure?.livingArea) || Number(subject.livingSquareFeet) || Number(subject.squareFootage) || 2000;
      const propertyBedrooms = Number(subject.propertyInfo?.bedrooms) || Number(subject.building?.rooms?.beds) || Number(subject.bedrooms) || 3;
      const propertyBathrooms = Number(subject.propertyInfo?.bathrooms) || Number(subject.building?.rooms?.baths) || Number(subject.bathrooms) || 3;

      // Populate the FREE widgets
      setSubjectPropertyInfo({
        livingSquareFeet: propertyLivingSqFt,
        bedrooms: propertyBedrooms,
        bathrooms: propertyBathrooms,
        basementType: toTitleCase(subject.propertyInfo?.basementType || subject.building?.basementType || 'N/A'),
      });

      // Use the API's built-in AVM for the free preview
      // Use ±10% for a tighter, more realistic confidence interval
      setPreviewEstimatedValue(estimatedValue);
      setAvmData({
        price: estimatedValue,
        priceRangeLow: Math.round(estimatedValue * 0.90),
        priceRangeHigh: Math.round(estimatedValue * 1.10),
      });

      // Cache subject ID and filters for Phase 2 (unlock) — no need to re-fetch
      setCachedSubjectId(subject.id);
      setCachedSubjectProperty(subject);
      setCachedDetailedProperty(subject);
      setCachedFilters(filters);

      // Extract the 1 teaser comp from the same response
      const firstComp = (compsData.comps || [])[0];
      if (firstComp) {
        setPreviewComps([{
          addressLine1: firstComp.address?.street || firstComp.address?.address || 'N/A',
          formattedAddress: firstComp.address?.address || 'N/A',
          price: parseFloat(firstComp.lastSaleAmount) || 0,
          soldDate: firstComp.lastSaleDate || null,
          squareFootage: Number(firstComp.squareFeet) || 0,
          bedrooms: Number(firstComp.bedrooms) || 0,
          bathrooms: Number(firstComp.bathrooms) || 0,
          distance: Number(firstComp.distance) || 0,
          pricePerSqFt: parseFloat(firstComp.lastSaleAmount) / (Number(firstComp.squareFeet) || 2000),
        }]);
      }

      // Mark free search as used
      if (!hasUsedFreeSearch) {
        localStorage.setItem('casalytix_free_search_used', 'true');
        setHasUsedFreeSearch(true);
      }

      setDataLoaded(true);
      setIsPreviewMode(true);
      setHasAnalyzed(true);
      setInputsDirty(false);
      console.log('Preview complete (1 credit). Estimated value:', estimatedValue);
    } catch (error) {
      console.error('Preview error:', error);
      setError(error.message);
      setSubjectPropertyInfo(null);
      setAvmData({ price: 0, priceRangeLow: 0, priceRangeHigh: 0 });
      setPreviewEstimatedValue(null);
      setDataLoaded(false);
    } finally {
      setIsAnalyzing(false);
    }
  };

  // ===== PHASE 2: PAID UNLOCK (1 API credit via v3/PropertyComps) =====
  const unlockFullAnalysis = async () => {
    if (!cachedSubjectProperty || !cachedFilters) {
      setError('No preview data found. Please analyze a property first.');
      return;
    }

    setIsUnlocking(true);
    setError(null);

    const filters = cachedFilters;

    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();

      const daysBack = filters.timeframe === 'Custom Date Range' && startDate && endDate
        ? Math.ceil((new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24))
        : { 'Last 3 months': 90, 'Last 6 months': 180, 'Last Year': 365 }[filters.timeframe] || 365;

      const isCondo = filters.propertyType.toUpperCase() === 'CONDO';
      const actualDaysBack = isCondo ? Math.max(daysBack, 365 * 5) : daysBack;

      // Extract subject property characteristics for smart comp filtering
      const subjectBeds = Number(cachedSubjectProperty.propertyInfo?.bedrooms) || 3;
      const subjectBaths = Number(cachedSubjectProperty.propertyInfo?.bathrooms) || 2;
      const subjectSqFt = Number(cachedSubjectProperty.propertyInfo?.livingSquareFeet) || 0;
      const subjectYearBuilt = Number(cachedSubjectProperty.propertyInfo?.yearBuilt) || 0;

      // Build payload using v3 custom params: ranges + boosts for better accuracy
      const compsPayload = {
        ...(cachedSubjectId ? { id: cachedSubjectId } : { address: selectedAddress.address }),
        max_radius_miles: parseInt(filters.radius) || 5,
        max_days_back: actualDaysBack,
        max_results: 50,
        arms_length: true,
        // Bed/bath ranges with boost (prioritize exact match, allow ±1 as fallback)
        bedrooms_min: Math.max(subjectBeds - 1, 1),
        bedrooms_max: subjectBeds + 1,
        bedrooms_boost: 50,
        bathrooms_min: Math.max(subjectBaths - 1, 1),
        bathrooms_max: subjectBaths + 1,
        bathrooms_boost: 50,
      };

      // Add living sqft range (±25%) if we have subject data
      if (subjectSqFt > 0) {
        compsPayload.living_square_feet_min = Math.round(subjectSqFt * 0.75);
        compsPayload.living_square_feet_max = Math.round(subjectSqFt * 1.25);
        compsPayload.living_square_feet_boost = 30;
      }

      // Add year built range (±15 years) if we have subject data
      if (subjectYearBuilt > 0) {
        compsPayload.year_built_min = subjectYearBuilt - 15;
        compsPayload.year_built_max = subjectYearBuilt + 15;
        compsPayload.year_built_boost = 20;
      }

      console.log('Unlock comps payload:', compsPayload);
      const compsUrl = `${BASE_API_URL}/propertyComps?idToken=${encodeURIComponent(idToken)}`;
      const compsResponse = await fetchWithTimeout(compsUrl, {
        method: 'POST',
        body: JSON.stringify(compsPayload)
      }, 15000);
      
      if (!compsResponse.ok) throw new Error(`Comps search failed: ${compsResponse.status}`);
      const compsData = await compsResponse.json();
      
      const returnedComps = compsData.comps || [];
      console.log(`Fetched ${returnedComps.length} valid comps from v3 API`);
      if (!returnedComps.length) throw new Error('No valid comparable sales found.');

      const soldComps = returnedComps.map(comp => ({
        addressLine1: comp.address?.street || comp.address?.address || 'N/A',
        formattedAddress: comp.address?.address || 'N/A',
        price: parseFloat(comp.lastSaleAmount) || 0,
        soldDate: comp.lastSaleDate,
        squareFootage: Number(comp.squareFeet) || 0,
        bedrooms: Number(comp.bedrooms) || 0,
        bathrooms: Number(comp.bathrooms) || 0,
        propertyType: comp.propertyType || filters.propertyType,
        pricePerSqFt: parseFloat(comp.lastSaleAmount) / (Number(comp.squareFeet) || 2000),
        distance: Number(comp.distance) || 0,
      }));

      console.log(`Found ${soldComps.length} valid comps`, soldComps);

      // Build chart data from comps
      const timeframeStart = filters.timeframe === 'Custom Date Range' ? new Date(startDate) : new Date(new Date().setDate(new Date().getDate() - daysBack));
      timeframeStart.setHours(0, 0, 0, 0);
      const timeframeEnd = filters.timeframe === 'Custom Date Range' ? new Date(endDate) : new Date();
      timeframeEnd.setHours(23, 59, 59, 999);

      const monthlyData = {};
      soldComps.forEach(comp => {
        const saleDate = new Date(comp.soldDate);
        saleDate.setHours(0, 0, 0, 0);
        if (saleDate >= timeframeStart && saleDate <= timeframeEnd) {
          const monthYear = saleDate.toLocaleString('default', { month: 'short', year: 'numeric' });
          if (!monthlyData[monthYear]) {
            monthlyData[monthYear] = { totalPrice: 0, count: 0 };
          }
          monthlyData[monthYear].totalPrice += comp.price;
          monthlyData[monthYear].count += 1;
        }
      });

      const labels = [];
      const avgPrices = [];
      const salesVolume = [];
      const startDateObj = new Date(timeframeStart);
      const endDateObj = new Date(timeframeEnd);
      for (let d = new Date(startDateObj); d <= endDateObj; d.setMonth(d.getMonth() + 1)) {
        const monthYear = d.toLocaleString('default', { month: 'short', year: 'numeric' });
        const data = monthlyData[monthYear] || { totalPrice: 0, count: 0 };
        if (data.count > 0) {
          labels.push(monthYear);
          avgPrices.push(Math.round(data.totalPrice / data.count));
          salesVolume.push(data.count);
        }
      }
      setChartData({ labels, avgPrices, salesVolume });

      // Use the native AVM provided by the v3/PropertyComps API, fallback to median comp price
      const compPrices = soldComps.map(comp => comp.price).filter(p => p > 0).sort((a, b) => a - b);
      const medianCompPrice = compPrices.length > 0
        ? (compPrices.length % 2 === 0
          ? (compPrices[compPrices.length / 2 - 1] + compPrices[compPrices.length / 2]) / 2
          : compPrices[Math.floor(compPrices.length / 2)])
        : 0;
      const avmPrice = Number(compsData.subject?.estimatedValue) || Math.round(medianCompPrice) || 0;

      // Price range: use comp min/max as the bounds (real data), constrained by ±15% of AVM
      const compMin = compPrices.length > 0 ? compPrices[0] : avmPrice * 0.90;
      const compMax = compPrices.length > 0 ? compPrices[compPrices.length - 1] : avmPrice * 1.10;
      setAvmData({
        price: avmPrice,
        priceRangeLow: Math.round(Math.min(compMin, avmPrice * 0.90)),
        priceRangeHigh: Math.round(Math.max(compMax, avmPrice * 1.10)),
      });

      setFilteredCompsData(soldComps);
      setRawCompsData(soldComps);
      setUnfilteredCompsData(soldComps);
      setIsPreviewMode(false);
      setHasUnlockedReport(true);
      setDataLoaded(true);
      console.log('Full analysis unlocked with', soldComps.length, 'comps (1 credit)');
    } catch (error) {
      console.error('Unlock analysis error:', error);
      setError(error.message);
    } finally {
      setIsUnlocking(false);
    }
  };

  // Handler called after successful Stripe payment
  const handleReportPaymentSuccess = async () => {
    setShowPaymentModal(false);
    await unlockFullAnalysis();
  };

  const handleUnlockWithCredit = async () => {
    try {
      await deductCredit();
      await unlockFullAnalysis();
    } catch (err) {
      console.error('Failed to unlock with credit:', err);
      setError('Failed to unlock with credit: ' + err.message);
    }
  };

  const showAppealInstructions = () => {
    setShowAppealModal(true);
  };

  const exportTaxAppealReport = () => {
    console.log('Exporting tax appeal report...');
    console.log('jsPDF available:', !!jsPDF);
    console.log('autoTable available:', !!autoTable);
    if (!filteredCompsData.length) {
      alert('No comparable sales available to export.');
      return;
    }

    try {
      const doc = new jsPDF();
      doc.setFontSize(16);
      doc.text('Property Tax Appeal Report', 10, 10);
      doc.setFontSize(12);
      doc.text(`Property Address: ${selectedAddress?.address || 'N/A'}`, 10, 20);
      doc.text(`Analysis Date: ${new Date().toLocaleDateString('en-US')}`, 10, 30);
      doc.text(`Estimated Value*: $${avmData?.price.toLocaleString() || 'N/A'} (Range: $${avmData?.priceRangeLow.toLocaleString() || 'N/A'} - $${avmData?.priceRangeHigh.toLocaleString() || 'N/A'})`, 10, 40);
      doc.text('To ensure accuracy, comparable sales are filtered to include only properties with a valid sale price and sale date, square footage within ±5% of your home’s square footage, and bedrooms and bathrooms within ±1 of your home. If your home has an estimated value above $1 million, the square footage range is relaxed to ±10%, the sale price range to ±30%, and bedrooms and bathrooms to ±2 (except for condos, which remain ±1), with no additional bedroom or bathroom filtering applied after initial data retrieval.', 10, 50, { maxWidth: 190 });

      doc.setFontSize(12);
      doc.text('Comparable Sales:', 10, 80);

      const tableData = filteredCompsData.map(comp => ({
        Address: comp.formattedAddress || 'N/A',
        'Sold Price': `$${comp.price.toLocaleString()}`,
        'Sale Date': comp.soldDate ? new Date(comp.soldDate).toLocaleDateString('en-US') : 'N/A',
        Distance: comp.distance ? `${comp.distance.toFixed(2)} mi` : 'N/A',
        '$ per Sq Ft': comp.pricePerSqFt ? `$${Math.round(comp.pricePerSqFt).toLocaleString()}` : 'N/A',
      }));

      autoTable(doc, {
        startY: 85,
        head: [['Address', 'Sold Price', 'Sale Date', 'Distance', '$ per Sq Ft']],
        body: tableData.map(row => [row.Address, row['Sold Price'], row['Sale Date'], row.Distance, row['$ per Sq Ft']]),
        styles: { fontSize: 10, cellPadding: 2 },
        columnStyles: {
          0: { cellWidth: 80 },
          1: { cellWidth: 30 },
          2: { cellWidth: 30 },
          3: { cellWidth: 20 },
          4: { cellWidth: 20 },
        },
        didDrawPage: (data) => {
          const pageHeight = doc.internal.pageSize.height;
          doc.setFontSize(10);
          doc.text('*Disclaimer: This estimated value is for informational purposes only. We cannot guarantee approval of your property tax appeal.', 10, pageHeight - 10, { maxWidth: 190 });
        },
      });

      doc.save('Comparable Sales.pdf');
      console.log('PDF exported successfully');
    } catch (err) {
      console.error('Export PDF error:', err);
      alert('Failed to export PDF: ' + err.message);
    }
  };

  const generateAiReport = async () => {
    if (!filteredCompsData.length) {
      alert('No comparable sales available to analyze. Please analyze a property first.');
      return;
    }

    setIsGeneratingReport(true);
    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();
      
      const effectivePropertyDetails = subjectPropertyInfo || { livingSquareFeet: 2633, bedrooms: 4, bathrooms: 4 };
      const subjPpsf = effectivePropertyDetails?.livingSquareFeet > 0 && assessedValue
        ? Math.round(Number(assessedValue) / effectivePropertyDetails.livingSquareFeet)
        : (avmData?.price && effectivePropertyDetails?.livingSquareFeet ? Math.round(avmData.price / effectivePropertyDetails.livingSquareFeet) : 0);

      const payload = {
        targetAddress: selectedAddress?.address || 'N/A',
        avm: avmData || { price: avmData?.price || 0 },
        assessedValue: assessedValue ? Number(assessedValue) : (avmData?.price || 0),
        estimatedValue: avmData?.price || 0,
        overassessmentAmount: (assessedValue && avmData?.price && Number(assessedValue) > avmData.price)
          ? Number(assessedValue) - avmData.price
          : 0,
        numComps: filteredCompsData.length,
        medianPrice: medianPrice || 0,
        avgPrice: avgPrice || 0,
        avgPricePerSqFt: avgPricePerSqFt || 0,
        subjectPpsf: subjPpsf,
        propertyDetails: effectivePropertyDetails,
        comps: filteredCompsData
      };

      const url = `${BASE_API_URL}/generate-ai-report?idToken=${encodeURIComponent(idToken)}`;
      
      const response = await fetchWithTimeout(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      }, 90000);

      if (!response.ok) {
        throw new Error(`Failed to generate report: ${response.status}`);
      }

      const data = await response.json();
      if (!data.report) {
         throw new Error('Invalid response received from AI service.');
      }

      // Export as an Editable Microsoft Word Document (.doc) so users can customize before filing
      exportWordDoc(data.report, selectedAddress?.address);
      console.log('AI report generated and downloaded as Word Document.');

    } catch (err) {
      console.error('Error generating AI report:', err);
      alert('Failed to generate AI report: ' + err.message);
    } finally {
      setIsGeneratingReport(false);
    }
  };

  // Helper function to export formal report as an Editable Word document (.doc)
  const exportWordDoc = (reportText, subjectAddress) => {
    const addressStr = subjectAddress || 'Subject Property';
    const cleanAddress = addressStr.replace(/[^a-zA-Z0-9]/g, '_');
    const dateStr = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

    let cleanText = (reportText || '')
      .replace(/\r\n/g, '\n')
      .replace(/[\u00A0\u1680\u180E\u2000-\u200B\u202F\u205F\u3000]/g, ' ')
      .replace(/\t/g, ' ')
      .replace(/\*\*/g, '')
      .replace(/###/g, '')
      .replace(/\[Licensed Real Estate Appraiser.*?\]/gi, '')
      .replace(/Licensed Real Estate Appraiser/gi, '');

    const escapeRtf = (str) => {
      return (str || '')
        .replace(/\\/g, '\\\\')
        .replace(/\{/g, '\\{')
        .replace(/\}/g, '\\}')
        .replace(/[^\x00-\x7F]/g, c => `\\u${c.charCodeAt(0)}?`);
    };

    let rtfContent = `{\\rtf1\\ansi\\deff0{\\fonttbl{\\f0\\fnil\\fcharset0 Calibri;}{\\f1\\fnil\\fcharset0 Arial;}}
{\\colortbl ;\\red15\\green23\\blue42;\\red2\\green132\\blue199;\\red71\\green85\\blue105;}
\\paperw12240\\paperh15840\\margl1440\\margr1440\\margt1440\\margb1440
\\f0\\fs36\\b\\cf1 PROPERTY TAX APPEAL REPORT\\b0\\par
\\fs22\\cf3\\i Subject Property: ${escapeRtf(addressStr)}\\i0\\par
\\i Date Prepared: ${dateStr}\\i0\\par\\par
\\line\\par\n`;

    const lines = cleanText.split('\n');
    lines.forEach(line => {
      const trimmed = line.trim();
      if (!trimmed) {
        rtfContent += '\\par\n';
        return;
      }

      const isHeader = /^(SECTION\\s+\\d+|1\\.\\s+INTRODUCTION|2\\.\\s+METHODOLOGY|3\\.\\s+COMPARABLE\\s+SALES|4\\.\\s+PRICE\\s+PER|5\\.\\s+CONCLUSION|INTRODUCTION|METHODOLOGY|COMPARABLE SALES SUMMARY|PRICE PER SQUARE FOOT ANALYSIS|CONCLUSION AND REQUESTED RELIEF)/i.test(trimmed);

      if (isHeader) {
        rtfContent += `\\par\\fs26\\b\\cf1 ${escapeRtf(trimmed)}\\b0\\par\\par\n`;
      } else if (trimmed.startsWith('Appellant Printed Name:')) {
        rtfContent += `\\par\\par\\fs22\\b Appellant Printed Name:\\b0  ___________________________________\\par\n`;
        rtfContent += `\\b Appellant Signature:\\b0  ___________________________________   \\b Date:\\b0  ____________\\par\n`;
      } else if (trimmed.startsWith('Appellant Signature:')) {
        // Skip duplicate line
      } else {
        rtfContent += `\\fs22\\cf1 ${escapeRtf(trimmed)}\\par\\par\n`;
      }
    });

    rtfContent += '}';

    const blob = new Blob([rtfContent], { type: 'application/rtf' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Property_Tax_Appeal_Report_${cleanAddress}.doc`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Derived market statistics calculated dynamically for React widgets
  const numComps = filteredCompsData.length;
  const compPrices = filteredCompsData.map(c => c.price).filter(p => p > 0).sort((a, b) => a - b);
  const medianPrice = compPrices.length > 0
    ? Math.round(compPrices.length % 2 === 0
      ? (compPrices[compPrices.length / 2 - 1] + compPrices[compPrices.length / 2]) / 2
      : compPrices[Math.floor(compPrices.length / 2)])
    : 0;
  const avgPrice = compPrices.length > 0
    ? Math.round(compPrices.reduce((sum, p) => sum + p, 0) / compPrices.length)
    : 0;
  const compPpsfs = filteredCompsData
    .map(c => c.pricePerSqFt || (c.squareFootage ? c.price / c.squareFootage : 0))
    .filter(p => p > 0);
  const avgPricePerSqFt = compPpsfs.length > 0
    ? Math.round(compPpsfs.reduce((sum, p) => sum + p, 0) / compPpsfs.length)
    : 0;

  // Determine if property is underassessed (assessed value is less than or equal to estimated market value)
  const isUnderassessed = Boolean(
    assessedValue && (avmData?.price || medianPrice) &&
    Number(assessedValue) <= (avmData?.price || medianPrice)
  );

  return (
    <div>
      <header className="header">
        <div className="header-content">
          <img src="/Casalytix Logo.png" alt="Casalytix Logo" className="logo" />
          <div className="navbar">
            <div className="hamburger-menu" onClick={() => setIsMenuOpen(!isMenuOpen)}>☰</div>
            <ul className={`nav-menu ${isMenuOpen ? 'block' : ''}`}>
              <li><a href="/">Home</a></li>
              <li><a href="/about">About</a></li>
              <li><a href="/pricing">Pricing</a></li>
              <li><a href="/contact">Contact</a></li>
              <li className="mobile-only">
                <span>Credits: {credits} {credits === 0 && <a href="/payment">Buy More Credits</a>}</span>
              </li>
              <li className="mobile-only">
                <a href="#" onClick={handleSignOut} className="sign-out-link">Sign out</a>
              </li>
            </ul>
          </div>
          <div className="desktop-only">
            <span>
              Credits: {credits} {credits === 0 && <a href="/payment" style={{ marginLeft: '10px' }}>Buy More Credits</a>}
            </span>
            <a href="#" onClick={handleSignOut} className="sign-out-link">Sign out</a>
          </div>
        </div>
      </header>
      <main className="dashboard-container">
        <div className="filter-section">
          <div style={{ position: 'relative', width: '100%', maxWidth: '700px' }}>
            <input
              type="text"
              placeholder="Enter your full property address (e.g. 123 Main St, City, ST 12345)"
              ref={addressInputRef}
              className="property-address"
              value={selectedAddress?.address || ''}
              onChange={handleAddressInputChange}
              onFocus={() => addressSuggestions.length > 0 && setShowSuggestions(true)}
              style={{ maxWidth: '700px', width: '100%', boxSizing: 'border-box' }}
              autoComplete="off"
            />
            {/* Google Places Autocomplete Dropdown */}
            {showSuggestions && addressSuggestions.length > 0 && (
              <ul
                ref={suggestionsRef}
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  zIndex: 9999,
                  background: 'white',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                  listStyle: 'none',
                  margin: '4px 0 0 0',
                  padding: '4px 0',
                  maxHeight: '220px',
                  overflowY: 'auto',
                }}
              >
                {addressSuggestions.map((s, index) => (
                  <li
                    key={s.place_id || s.id || index}
                    onClick={() => handleSuggestionSelect(s)}
                    style={{
                      padding: '10px 16px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      color: '#1e293b',
                      fontWeight: '500',
                      borderBottom: '1px solid #f1f5f9',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      textAlign: 'left',
                      transition: 'background 0.15s',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#f0f9ff'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                  >
                    <span>📍</span>
                    <span>{s.address}</span>
                  </li>
                ))}
              </ul>
            )}
            <div style={{ marginTop: '6px', textAlign: 'left' }}>
              <button
                type="button"
                onClick={() => setShowAddressGuideModal(true)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#2563eb',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  fontSize: '13px',
                  padding: 0,
                  fontWeight: '500',
                }}
              >
                Proper Address Format Guidelines
              </button>
            </div>
          </div>
          <input
            type="text"
            placeholder="Current Assessed Value (e.g. 350000)"
            value={assessedValue}
            onChange={(e) => setAssessedValue(e.target.value.replace(/[^0-9]/g, ''))}
            style={{ maxWidth: '300px', width: '100%', boxSizing: 'border-box', padding: '8px 12px', border: '1px solid #ccc', borderRadius: '5px', fontSize: '14px', marginTop: '8px' }}
          />
          <select id="timeframe" value={timeframe} onChange={(e) => { setTimeframe(e.target.value); setInputsDirty(true); }}>
            <option value="Leave Blank">Leave Blank</option>
            <option value="Last 3 months">Last 3 months</option>
            <option value="Last 6 months">Last 6 months</option>
            <option value="Last Year">Last 365 Days</option>
            <option value="Custom Date Range">Custom Date Range</option>
          </select>
          {timeframe === 'Custom Date Range' && (
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <div>
                <label htmlFor="startDate" style={{ marginRight: '5px' }}>Start:</label>
                <input
                  type="date"
                  id="startDate"
                  value={startDate}
                  onChange={(e) => {
                    const newStart = e.target.value;
                    setStartDate(newStart);
                    if (endDate && new Date(newStart) > new Date(endDate)) {
                      setEndDate('');
                    }
                  }}
                  max={new Date().toISOString().split('T')[0]}
                  style={{ padding: '5px', width: '140px' }}
                />
              </div>
              <div>
                <label htmlFor="endDate" style={{ marginRight: '5px' }}>End:</label>
                <input
                  type="date"
                  id="endDate"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  min={startDate || undefined}
                  max={new Date().toISOString().split('T')[0]}
                  style={{ padding: '5px', width: '140px' }}
                  disabled={!startDate}
                />
              </div>
            </div>
          )}
          <select id="radius" defaultValue="5 miles" onChange={() => setInputsDirty(true)}>
            <option value="1 miles">1 mile</option>
            <option value="2 miles">2 miles</option>
            <option value="5 miles">5 miles</option>
            <option value="10 miles">10 miles</option>
          </select>
          <select id="property-type" defaultValue="Single Family Residence" onChange={() => setInputsDirty(true)}>
            <option value="Leave Blank">Leave Blank</option>
            <option value="Single Family Residence">Single Family</option>
            <option value="Condo">Condo</option>
            <option value="Townhouse">Townhouse</option>
            <option value="Multi Family">Multi-Family (2–4 Units)</option>
          </select>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'nowrap', overflowX: 'auto' }}>
            <button
              onClick={previewProperty}
              disabled={isAnalyzing || (hasAnalyzed && !inputsDirty)}
              style={{
                background: (isAnalyzing || (hasAnalyzed && !inputsDirty)) ? '#94a3b8' : 'linear-gradient(135deg, #0ea5e9, #2563eb)',
                color: '#fff',
                border: 'none',
                padding: '7px 16px',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: '600',
                cursor: (isAnalyzing || (hasAnalyzed && !inputsDirty)) ? 'not-allowed' : 'pointer',
                boxShadow: (isAnalyzing || (hasAnalyzed && !inputsDirty)) ? 'none' : '0 3px 10px rgba(14, 165, 233, 0.4)',
                transition: 'all 0.25s ease',
                letterSpacing: '0.3px',
                whiteSpace: 'nowrap',
              }}
              onMouseOver={(e) => { if (!isAnalyzing && !(hasAnalyzed && !inputsDirty)) { e.target.style.transform = 'translateY(-2px)'; } }}
              onMouseOut={(e) => { e.target.style.transform = 'translateY(0)'; }}
            >
              {isAnalyzing ? 'Analyzing...' : (hasAnalyzed && !inputsDirty) ? '✓ Analyzed' : (credits > 0 ? 'Analyze Property' : 'Free Estimate')}
            </button>
            {hasUnlockedReport && (
              <>
                <button onClick={exportTaxAppealReport} disabled={!dataLoaded} style={{
                  background: dataLoaded ? 'linear-gradient(135deg, #3b82f6, #1d4ed8)' : '#cbd5e1',
                  color: '#fff',
                  border: 'none',
                  padding: '7px 16px',
                  borderRadius: '8px',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: dataLoaded ? 'pointer' : 'not-allowed',
                  boxShadow: dataLoaded ? '0 3px 10px rgba(59, 130, 246, 0.35)' : 'none',
                  transition: 'all 0.25s ease',
                  whiteSpace: 'nowrap',
                }}
                  onMouseOver={(e) => { if (dataLoaded) { e.target.style.transform = 'translateY(-2px)'; } }}
                  onMouseOut={(e) => { e.target.style.transform = 'translateY(0)'; }}
                >
                  Export Comps
                </button>
                <button
                  onClick={generateAiReport}
                  disabled={!dataLoaded || isGeneratingReport || isUnderassessed}
                  title={isUnderassessed ? "Property is underassessed or fairly assessed — no tax appeal report needed." : ""}
                  style={{
                    background: dataLoaded && !isGeneratingReport && !isUnderassessed
                      ? 'linear-gradient(135deg, #10b981, #059669)'
                      : '#cbd5e1',
                    color: '#fff',
                    border: 'none',
                    padding: '7px 16px',
                    borderRadius: '8px',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: dataLoaded && !isGeneratingReport && !isUnderassessed ? 'pointer' : 'not-allowed',
                    boxShadow: dataLoaded && !isGeneratingReport && !isUnderassessed ? '0 3px 10px rgba(16, 185, 129, 0.35)' : 'none',
                    transition: 'all 0.25s ease',
                    whiteSpace: 'nowrap',
                  }}
                  onMouseOver={(e) => { if (dataLoaded && !isGeneratingReport && !isUnderassessed) { e.target.style.transform = 'translateY(-2px)'; } }}
                  onMouseOut={(e) => { e.target.style.transform = 'translateY(0)'; }}
                >
                  {isUnderassessed
                    ? 'No Appeal Needed (Underassessed)'
                    : (isGeneratingReport ? 'Generating Full Report...' : 'Generate Full Report')}
                </button>
              </>
            )}
            <button
              onClick={showAppealInstructions}
              style={{
                background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)',
                color: '#fff',
                border: 'none',
                padding: '7px 16px',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer',
                boxShadow: '0 3px 10px rgba(139, 92, 246, 0.35)',
                transition: 'all 0.25s ease',
                letterSpacing: '0.3px',
                whiteSpace: 'nowrap',
              }}
              onMouseOver={(e) => { e.target.style.transform = 'translateY(-2px)'; }}
              onMouseOut={(e) => { e.target.style.transform = 'translateY(0)'; }}
            >
              How to Appeal
            </button>
          </div>
        </div>
        {hasUnlockedReport && dataLoaded && filteredCompsData.length > 0 && (
          <div className="comparable-note" style={{ fontSize: '12px', margin: '16px auto 20px auto', lineHeight: '1.7', color: '#64748b', textAlign: 'left', background: '#f8fafc', padding: '12px 16px', borderRadius: '8px', border: '1px solid #e2e8f0', maxWidth: '680px' }}>
            <strong>Comparable Filtering Criteria:</strong> To ensure accuracy, comparable sales are filtered to include only properties with a valid sale price and sale date, square footage within ±5% of your home's square footage, and bedrooms and bathrooms within ±1 of your home. If your home has an estimated value above $1M, the square footage range is relaxed to ±10%, the sale price range to ±30%, and bedrooms and bathrooms to ±2 (except for condos, which remain ±1).
          </div>
        )}

        {/* ===== DATA SOURCES & VALUATION METHODOLOGY EXPLANATION BOX ===== */}
        {dataLoaded && (
          <div style={{
            background: '#f0f9ff',
            border: '1px solid #bae6fd',
            borderRadius: '12px',
            padding: '16px 20px',
            marginTop: '16px',
            marginBottom: '24px',
            maxWidth: '900px',
            margin: '16px auto 24px auto',
            textAlign: 'left',
            fontSize: '13px',
            color: '#0369a1',
            lineHeight: '1.6'
          }}>
            <div style={{ fontWeight: '700', color: '#0c4a6e', marginBottom: '8px', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              💡 Understanding Your Valuation & Property Data
            </div>
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              <li style={{ marginBottom: '6px' }}>
                <strong>Official County Assessor Records:</strong> Property specs (sq ft, beds, baths) are pulled directly from official county tax assessor public records — the exact specs used by county boards for tax assessment appeals.
              </li>
              <li>
                <strong>Estimated AVM Value vs. Comparable Sales:</strong> The <em>Estimated Value</em> is fetched directly from RealEstateAPI's Automated Valuation Model (AVM) using broad tax indexing across your zip code. The <em>Median & Average Sold Prices</em> are derived strictly from your nearby filtered comparable sales. <strong>If your county AVM estimate is higher than nearby comp sales, you have clear evidence of an overassessment!</strong>
              </li>
            </ul>
          </div>
        )}

        {(isAnalyzing || isUnlocking) && (
          <div className="analyzing-overlay">
            <div className="spinner"></div>
            <p>{isUnlocking ? 'Unlocking full report...' : 'Analyzing your property...'}</p>
          </div>
        )}
        {error && <div style={{ color: 'red', padding: '10px' }}>Error: {error}</div>}

        {/* ===== DIAGNOSIS HEADLINE (visible for free users) ===== */}
        {dataLoaded && isPreviewMode && (
          <div style={{ textAlign: 'center', padding: '24px 16px 8px', maxWidth: '700px', margin: '0 auto' }}>
            <h2 style={{ fontSize: '26px', fontWeight: '700', color: '#1e293b', margin: '0 0 6px 0' }}>
              {assessedValue && avmData?.price && Number(assessedValue) > avmData.price
                ? '⚠️ You may be overpaying property taxes.'
                : '📊 Your Property Valuation Results'}
            </h2>
            <p style={{ fontSize: '15px', color: '#64748b', margin: 0 }}>
              Based on nearby comparable sales and your current assessment.
            </p>
          </div>
        )}

        {/* ===== FREE & PREMIUM WIDGETS SECTION ===== */}
        <div className={`widgets-section ${!dataLoaded ? 'widget-hidden' : ''}`}>
          {/* DEDICATED SUBJECT PROPERTY DETAILS WIDGET */}
          <div className="widget number-widget">
            <h3 style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', margin: '0 0 10px 0' }}>
              Subject Property Details
              <button
                type="button"
                onClick={() => setInfoModal({
                  title: 'Subject Property Details',
                  description: 'Property specifications (living area, bedrooms, bathrooms) retrieved directly from official county tax assessor public records. These are the official specs used by county tax boards for assessment appeals.'
                })}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.85em', opacity: 0.8, padding: '0 2px' }}
              >
                ℹ️
              </button>
            </h3>
            <div className="number-value" style={{ width: '100%' }}>
              {subjectPropertyInfo ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'center' }}>
                  <div style={{ fontSize: '0.8em', color: '#1e293b', fontWeight: '600' }}>
                    Sq Ft: {subjectPropertyInfo.livingSquareFeet ? subjectPropertyInfo.livingSquareFeet.toLocaleString() : 'N/A'}
                  </div>
                  <div style={{ fontSize: '0.75em', color: '#475569' }}>Beds: {subjectPropertyInfo.bedrooms || 'N/A'} | Baths: {subjectPropertyInfo.bathrooms || 'N/A'}</div>
                  <div style={{ fontSize: '0.75em', color: '#475569' }}>Basement: {subjectPropertyInfo.basementType || 'N/A'}</div>
                  <div style={{ fontSize: '0.65em', color: '#94a3b8', marginTop: '4px' }}>Official County Tax Record</div>
                </div>
              ) : 'N/A'}
            </div>
          </div>

          {/* POTENTIAL OVERASSESSMENT WIDGET */}
          <div className="widget number-widget">
            <h3 style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
              Potential Overassessment
              <button
                type="button"
                onClick={() => setInfoModal({
                  title: 'Potential Overassessment',
                  description: 'The difference between your county current assessed value and the market valuation derived from nearby sales. Highlighting this gap is key to winning a tax reduction.'
                })}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.85em', opacity: 0.8, padding: '0 2px' }}
              >
                ℹ️
              </button>
            </h3>
            <div className="number-value">
              {assessedValue && avmData?.price ? (
                <>
                  <div style={{ fontSize: '0.7em', color: '#64748b' }}>Assessed: ${Number(assessedValue).toLocaleString()}</div>
                  <div style={{ fontSize: '0.7em', color: '#64748b' }}>Estimated: ${avmData.price.toLocaleString()}</div>
                  <div style={{
                    fontSize: '0.85em',
                    fontWeight: '700',
                    color: Number(assessedValue) > avmData.price ? '#dc2626' : '#16a34a',
                    marginTop: '4px'
                  }}>
                    {Number(assessedValue) > avmData.price
                      ? `Overpaying by $${(Number(assessedValue) - avmData.price).toLocaleString()}`
                      : `Fair or under-assessed`}
                  </div>
                </>
              ) : (
                <div style={{ fontSize: '0.75em', color: '#64748b' }}>Enter your Assessed Value above to calculate overassessment.</div>
              )}
            </div>
          </div>

          <div className="widget number-widget">
            <h3 style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
              Estimated Value
              <button
                type="button"
                onClick={() => setInfoModal({
                  title: 'Estimated Value (AVM)',
                  description: 'Automated Valuation Model estimate fetched directly from RealEstateAPI (sourced from county tax & property records). It uses broad tax indexing across your zip code, which often differs from actual nearby comp sales — giving you proof of overassessment!'
                })}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.85em', opacity: 0.8, padding: '0 2px' }}
              >
                ℹ️
              </button>
            </h3>
            <div className="number-value">
              {avmData?.price ? (
                <>
                  ${avmData.price.toLocaleString()}
                  <div style={{ fontSize: '0.6em', color: '#64748b', fontWeight: 'normal', marginTop: '4px' }}>
                    Range: ${avmData.priceRangeLow.toLocaleString()} - ${avmData.priceRangeHigh.toLocaleString()}
                  </div>
                </>
              ) : '$N/A'}
            </div>
          </div>

          {/* PREMIUM NUMBER WIDGETS */}
          <div className="widget number-widget" style={isPreviewMode ? { filter: 'blur(8px)', pointerEvents: 'none', userSelect: 'none' } : {}}>
            <h3 style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
              Number of Comps
              <button
                type="button"
                onClick={() => setInfoModal({
                  title: 'Number of Comps',
                  description: 'The total count of nearby, recently sold comparable homes matching your property type, size, and location criteria.'
                })}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.85em', opacity: 0.8, padding: '0 2px' }}
              >
                ℹ️
              </button>
            </h3>
            <div className="number-value">{isPreviewMode ? '—' : numComps}</div>
          </div>

          <div className="widget number-widget" style={isPreviewMode ? { filter: 'blur(8px)', pointerEvents: 'none', userSelect: 'none' } : {}}>
            <h3 style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
              Median Sold Price
              <button
                type="button"
                onClick={() => setInfoModal({
                  title: 'Median Sold Price',
                  description: 'The middle sale price of all analyzed comparable sales. The median is the primary benchmark used in formal assessment appeals because it is immune to extreme high or low price outliers.'
                })}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.85em', opacity: 0.8, padding: '0 2px' }}
              >
                ℹ️
              </button>
            </h3>
            <div className="number-value">{isPreviewMode ? '$—' : (medianPrice ? `$${medianPrice.toLocaleString()}` : '$N/A')}</div>
          </div>

          <div className="widget number-widget" style={isPreviewMode ? { filter: 'blur(8px)', pointerEvents: 'none', userSelect: 'none' } : {}}>
            <h3 style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
              Average Sold Price
              <button
                type="button"
                onClick={() => setInfoModal({
                  title: 'Average Sold Price',
                  description: 'The mathematical average sale price across all analyzed comparable properties in your immediate market area.'
                })}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.85em', opacity: 0.8, padding: '0 2px' }}
              >
                ℹ️
              </button>
            </h3>
            <div className="number-value">{isPreviewMode ? '$—' : (avgPrice ? `$${avgPrice.toLocaleString()}` : '$N/A')}</div>
          </div>

          <div className="widget number-widget" style={isPreviewMode ? { filter: 'blur(8px)', pointerEvents: 'none', userSelect: 'none' } : {}}>
            <h3 style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
              Avg Price/Sq Ft
              <button
                type="button"
                onClick={() => setInfoModal({
                  title: 'Average Price Per Sq Ft',
                  description: 'The average price per square foot of living area among comparable sales. Multiplying this rate by your home square footage provides an unassailable valuation for tax appeal boards.'
                })}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.85em', opacity: 0.8, padding: '0 2px' }}
              >
                ℹ️
              </button>
            </h3>
            <div className="number-value">{isPreviewMode ? '$—' : (avgPricePerSqFt ? `$${avgPricePerSqFt.toLocaleString()}/sq ft` : '$N/A')}</div>
          </div>



          {/* Unlock CTA Banner */}
          {isPreviewMode && (
            <div style={{
              width: '100%',
              background: 'linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%)',
              borderRadius: '12px',
              padding: '30px',
              textAlign: 'center',
              color: 'white',
              margin: '10px 0',
              boxShadow: '0 4px 20px rgba(37, 99, 235, 0.3)',
            }}>
              <div style={{ fontSize: '28px', marginBottom: '8px' }}>🔒</div>
              <h3 style={{ margin: '0 0 8px 0', fontSize: '22px', fontWeight: '700' }}>Unlock Full Analysis</h3>
              <p style={{ margin: '0 0 16px 0', opacity: 0.9, fontSize: '14px', maxWidth: '500px', marginLeft: 'auto', marginRight: 'auto' }}>
                Get full comparable sales analysis, market trend charts, and a property tax appeal report — everything you need to fight your assessment.
              </p>
              {credits > 0 ? (
                <button
                  onClick={handleUnlockWithCredit}
                  style={{
                    background: '#0ea5e9',
                    color: 'white',
                    border: 'none',
                    padding: '14px 40px',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: '700',
                    cursor: 'pointer',
                    transition: 'transform 0.2s, background-color 0.2s',
                    boxShadow: '0 4px 12px rgba(14, 165, 233, 0.3)',
                  }}
                  onMouseOver={(e) => {
                    e.target.style.transform = 'scale(1.05)';
                    e.target.style.backgroundColor = '#0284c7';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.transform = 'scale(1)';
                    e.target.style.backgroundColor = '#0ea5e9';
                  }}
                >
                  Unlock Report (1 Credit)
                </button>
              ) : (
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
                  <button
                    onClick={() => { window.location.href = '/payment?plan=single'; }}
                    style={{
                      background: 'white',
                      color: '#1e3a5f',
                      border: '2px solid white',
                      padding: '12px 24px',
                      borderRadius: '8px',
                      fontSize: '15px',
                      fontWeight: '700',
                      cursor: 'pointer',
                      transition: 'transform 0.2s',
                    }}
                    onMouseOver={(e) => e.target.style.transform = 'scale(1.03)'}
                    onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
                  >
                    Unlock 1 Report ($39)
                  </button>
                  <button
                    onClick={() => { window.location.href = '/payment?plan=pack5'; }}
                    style={{
                      background: '#0ea5e9',
                      color: 'white',
                      border: 'none',
                      padding: '12px 28px',
                      borderRadius: '8px',
                      fontSize: '15px',
                      fontWeight: '700',
                      cursor: 'pointer',
                      transition: 'transform 0.2s',
                      boxShadow: '0 4px 12px rgba(14, 165, 233, 0.4)',
                    }}
                    onMouseOver={(e) => e.target.style.transform = 'scale(1.03)'}
                    onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
                  >
                    Go Premium — $79 for 5 Reports (Save 60%)
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Blurred charts in preview mode - dedicated second row */}
          <div className="charts-section">
            <div className="widget chart-widget" style={isPreviewMode ? { filter: 'blur(8px)', pointerEvents: 'none', userSelect: 'none' } : {}}>
              <h3 style={{ margin: '0 0 12px 0', fontSize: '18px', fontWeight: '700', color: '#1e293b' }}>Average Sales Price Trend</h3>
              <div className="chart-container">
                <Line
                  data={{
                    labels: isPreviewMode ? ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'] : chartData.labels,
                    datasets: [{
                      label: 'Average Sales Price',
                      data: isPreviewMode ? [350000, 365000, 360000, 370000, 380000, 375000] : chartData.avgPrices,
                      borderColor: '#007BFF',
                      backgroundColor: 'rgba(0, 123, 255, 0.1)',
                      fill: true,
                    }],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    scales: {
                      y: { beginAtZero: false, title: { display: true, text: 'Price ($)' } },
                      x: { title: { display: true, text: 'Month' } },
                    },
                  }}
                />
              </div>
            </div>
            <div className="widget chart-widget" style={isPreviewMode ? { filter: 'blur(8px)', pointerEvents: 'none', userSelect: 'none' } : {}}>
              <h3 style={{ margin: '0 0 12px 0', fontSize: '18px', fontWeight: '700', color: '#1e293b' }}>Monthly Sales Volume</h3>
              <div className="chart-container">
                <Bar
                  data={{
                    labels: isPreviewMode ? ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'] : chartData.labels,
                    datasets: [{
                      label: 'Number of Sales',
                      data: isPreviewMode ? [5, 8, 6, 9, 7, 10] : chartData.salesVolume,
                      backgroundColor: '#007BFF',
                    }],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    scales: {
                      y: { beginAtZero: true, title: { display: true, text: 'Sales Count' } },
                      x: { title: { display: true, text: 'Month' } },
                    },
                  }}
                />
              </div>
            </div>
          </div>
          <div className="widget table-widget" style={isPreviewMode ? { pointerEvents: 'none', userSelect: 'none' } : {}}>
            <h3>Comparable Sales</h3>
            <div className="comps-grid">
              {isPreviewMode && previewComps.length > 0 && (
                <>
                  {/* First comp row shown clearly */}
                  <div className="comp-card" style={{ marginBottom: '10px' }}>
                    <div className="comp-info">
                      <p><strong>Address:</strong> {previewComps[0].addressLine1 || previewComps[0].formattedAddress || 'N/A'}</p>
                      <p><strong>Sold Price:</strong> ${previewComps[0].price ? previewComps[0].price.toLocaleString() : 'N/A'}</p>
                      <p><strong>Sale Date:</strong> {previewComps[0].soldDate ? new Date(previewComps[0].soldDate).toLocaleDateString('en-US') : 'N/A'}</p>
                    </div>
                  </div>
                  {/* Blurred placeholder rows */}
                  {[1, 2, 3].map(i => (
                    <div key={i} className="comp-card" style={{ filter: 'blur(6px)', marginBottom: '10px' }}>
                      <div className="comp-info">
                        <p><strong>Address:</strong> 123 Sample Street</p>
                        <p><strong>Sold Price:</strong> $350,000</p>
                        <p><strong>Sale Date:</strong> 01/15/2025</p>
                      </div>
                    </div>
                  ))}
                  <div style={{ textAlign: 'center', padding: '10px', color: '#64748b', fontSize: '13px' }}>
                    🔒 Unlock to see all comparable sales
                  </div>
                </>
              )}
              {isPreviewMode && previewComps.length === 0 && (
                <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                  Comparable sales data will appear here after unlocking.
                </div>
              )}

              {!isPreviewMode && filteredCompsData.length > 0 && (
                filteredCompsData.map((comp, idx) => (
                  <div key={comp.id || idx} className="comp-card" style={{ marginBottom: '12px', padding: '14px', borderRadius: '8px', border: '1px solid #e2e8f0', background: '#ffffff' }}>
                    <div className="comp-info">
                      <p style={{ margin: '0 0 4px 0', fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>
                        {comp.addressLine1 || comp.formattedAddress || 'N/A'}
                      </p>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '6px 12px', fontSize: '13px', color: '#475569', marginTop: '6px' }}>
                        <div><strong>Sold Price:</strong> ${comp.price ? comp.price.toLocaleString() : 'N/A'}</div>
                        <div><strong>Sale Date:</strong> {comp.soldDate ? new Date(comp.soldDate).toLocaleDateString('en-US') : 'N/A'}</div>
                        <div><strong>Sq Ft:</strong> {comp.squareFootage ? comp.squareFootage.toLocaleString() : 'N/A'}</div>
                        <div><strong>Beds/Baths:</strong> {comp.bedrooms || 0} bd / {comp.bathrooms || 0} ba</div>
                        <div><strong>Distance:</strong> {comp.distance ? comp.distance.toFixed(2) : '0.00'} mi</div>
                        <div><strong>$/Sq Ft:</strong> ${comp.pricePerSqFt ? Math.round(comp.pricePerSqFt).toLocaleString() : 'N/A'}</div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* ===== HOW TO APPEAL MODAL ===== */}
        {showAppealModal && (
          <div style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.6)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px',
          }}>
            <div style={{
              background: 'white',
              borderRadius: '16px',
              padding: '28px 24px',
              maxWidth: '560px',
              width: '100%',
              maxHeight: '85vh',
              overflowY: 'auto',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
              position: 'relative',
            }}>
              <button
                onClick={() => setShowAppealModal(false)}
                style={{ position: 'absolute', top: '16px', right: '16px', background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#94a3b8', padding: '0', lineHeight: '1' }}
              >×</button>
              
              <div style={{ marginBottom: '20px', paddingRight: '24px' }}>
                <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: '#1e293b', lineHeight: '1.3', textAlign: 'left' }}>How to Appeal Your Property Taxes</h2>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
                {[
                  { step: 1, title: 'Research Your Deadline', body: 'Visit your county\'s appraisal or assessment district website to find the appeal filing deadline. Most states allow 30-90 days after you receive your assessment notice. Missing this deadline forfeits your right to appeal for the year.' },
                  { step: 2, title: 'Gather Your Evidence', body: 'Use Casalytix to build your case. Your Estimated Market Value, Comparable Sales, and Export Comps PDF are the three most powerful tools in a tax appeal. Focus on comps that sold below your assessed value.' },
                  { step: 3, title: 'File the Appeal Form', body: 'Download and complete your county\'s property tax protest or appeal form. Attach the Casalytix PDF report as supporting evidence. Many counties allow online filing through their Appraisal Review Board (ARB) portal.' },
                  { step: 4, title: 'Attend the Hearing', body: 'Present your comparable sales data and estimated value at the informal or formal hearing. Be concise and fact-based - show the appraiser that similar homes in your area sold for less than your assessed value.' },
                  { step: 5, title: 'Accept or Escalate', body: 'If the initial decision doesn\'t meet your expectations, you typically have the option to escalate to a formal hearing or state-level review board. Many homeowners win at the informal level with good data.' },
                ].map(({ step, title, body }) => (
                  <div key={step} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                    <div style={{ background: 'linear-gradient(135deg, #0ea5e9, #2563eb)', color: '#fff', borderRadius: '50%', width: '32px', height: '32px', minWidth: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700', fontSize: '14px', marginTop: '2px' }}>{step}</div>
                    <div style={{ textAlign: 'left' }}>
                      <h4 style={{ margin: '0 0 4px 0', fontSize: '15px', fontWeight: '600', color: '#1e293b' }}>{title}</h4>
                      <p style={{ margin: 0, fontSize: '13px', color: '#64748b', lineHeight: '1.65', textAlign: 'left' }}>{body}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ marginTop: '22px', padding: '14px 16px', background: '#f8fafc', borderRadius: '10px', border: '1px solid #e2e8f0', textAlign: 'left' }}>
                <p style={{ margin: 0, fontSize: '13px', color: '#475569', lineHeight: '1.6', textAlign: 'left' }}>
                  <strong>Pro Tip:</strong> Homeowners who bring 3-5 comparable sales as evidence are significantly more likely to receive a reduction. Casalytix's comparable sales report is designed specifically for this purpose.
                </p>
              </div>

              <button
                onClick={() => setShowAppealModal(false)}
                style={{
                  marginTop: '20px',
                  width: '100%',
                  background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)',
                  color: '#fff',
                  border: 'none',
                  padding: '14px',
                  borderRadius: '10px',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  boxShadow: '0 4px 14px rgba(139, 92, 246, 0.35)',
                  transition: 'all 0.25s ease',
                }}
                onMouseOver={(e) => { e.target.style.transform = 'translateY(-1px)'; e.target.style.boxShadow = '0 6px 20px rgba(139, 92, 246, 0.5)'; }}
                onMouseOut={(e) => { e.target.style.transform = 'translateY(0)'; e.target.style.boxShadow = '0 4px 14px rgba(139, 92, 246, 0.35)'; }}
              >
                Got It
              </button>
            </div>
          </div>
        )}

        {/* ===== ADDRESS FORMAT GUIDELINES MODAL ===== */}
        {showAddressGuideModal && (
          <div style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.6)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px',
          }}>
            <div style={{
              background: 'white',
              borderRadius: '16px',
              padding: '28px 24px',
              maxWidth: '520px',
              width: '100%',
              maxHeight: '85vh',
              overflowY: 'auto',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
              position: 'relative',
            }}>
              <button
                onClick={() => setShowAddressGuideModal(false)}
                style={{ position: 'absolute', top: '16px', right: '16px', background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#94a3b8', padding: '0', lineHeight: '1' }}
              >×</button>

              <div style={{ marginBottom: '16px', paddingRight: '24px' }}>
                <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: '#1e293b', lineHeight: '1.3', textAlign: 'left' }}>
                  Proper Address Format Guidelines
                </h2>
              </div>

              <p style={{ fontSize: '14px', color: '#475569', lineHeight: '1.6', textAlign: 'left', marginTop: 0 }}>
                To ensure high accuracy when searching public property records, enter full, standard US postal addresses.
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '16px', textAlign: 'left' }}>
                <div style={{ padding: '12px 14px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                  <div style={{ fontWeight: '600', fontSize: '13px', color: '#0f172a', marginBottom: '4px' }}>Single Family Home Example:</div>
                  <div style={{ fontFamily: 'monospace', fontSize: '13px', color: '#2563eb' }}>6806 19th Rd N, Arlington, VA 22205</div>
                </div>

                <div style={{ padding: '12px 14px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                  <div style={{ fontWeight: '600', fontSize: '13px', color: '#0f172a', marginBottom: '4px' }}>Condo / Apartment Unit Example:</div>
                  <div style={{ fontFamily: 'monospace', fontSize: '13px', color: '#2563eb' }}>100 Ocean Dr Apt 4B, Miami, FL 33139</div>
                </div>

                <div style={{ padding: '12px 14px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                  <div style={{ fontWeight: '600', fontSize: '13px', color: '#0f172a', marginBottom: '4px' }}>Best Practices:</div>
                  <ul style={{ margin: '4px 0 0 0', paddingLeft: '20px', fontSize: '13px', color: '#64748b', lineHeight: '1.5' }}>
                    <li>Include House Number, Street Name, City, 2-letter State, and 5-digit ZIP Code.</li>
                    <li>Separate major components with commas (e.g. Street, City, ST ZIP).</li>
                    <li>Avoid abbreviations that omit the street type (e.g. use "Main St" or "Main Street", not just "Main").</li>
                  </ul>
                </div>
              </div>

              <button
                onClick={() => setShowAddressGuideModal(false)}
                style={{
                  marginTop: '20px',
                  width: '100%',
                  background: 'linear-gradient(135deg, #0ea5e9, #2563eb)',
                  color: '#fff',
                  border: 'none',
                  padding: '12px',
                  borderRadius: '10px',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  boxShadow: '0 4px 14px rgba(14, 165, 233, 0.35)',
                  transition: 'all 0.25s ease',
                }}
              >
                Close
              </button>
            </div>
          </div>
        )}

        {/* ===== INLINE PAYMENT MODAL ===== */}
        {showPaymentModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.6)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '30px',
              maxWidth: '480px',
              width: '90%',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h3 style={{ margin: 0 }}>Go Premium — $79 for 5 Reports</h3>
                <button
                  onClick={() => setShowPaymentModal(false)}
                  style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#999' }}
                >×</button>
              </div>
              <p style={{ color: '#666', marginBottom: '20px', fontSize: '14px' }}>
                Unlock 5 full property analyses — each includes comparable sales, market trends, and a property tax appeal report. Use them anytime.
              </p>
              <Elements stripe={stripePromise}>
                <InlineCheckoutForm
                  userId={userId}
                  address={selectedAddress?.address}
                  onSuccess={handleReportPaymentSuccess}
                  onError={(msg) => setError(msg)}
                />
              </Elements>
            </div>
          </div>
        )}
      </main>



      {/* ===== INTERACTIVE (i) INFO MODAL FOR MOBILE & DESKTOP ===== */}
      {infoModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10002, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
          <div style={{ background: 'white', borderRadius: '16px', padding: '24px', maxWidth: '400px', width: '100%', boxShadow: '0 20px 50px rgba(0,0,0,0.25)', textAlign: 'center' }}>
            <h3 style={{ margin: '0 0 10px 0', fontSize: '18px', color: '#0f172a' }}>ℹ️ {infoModal.title}</h3>
            <p style={{ margin: '0 0 20px 0', fontSize: '14px', color: '#475569', lineHeight: 1.6 }}>{infoModal.description}</p>
            <button
              onClick={() => setInfoModal(null)}
              style={{ padding: '10px 24px', background: '#0ea5e9', color: 'white', border: 'none', borderRadius: '8px', fontWeight: '600', cursor: 'pointer', fontSize: '14px' }}
            >
              Got It
            </button>
          </div>
        </div>
      )}

      {/* ===== DISCLAIMER FOOTER ===== */}
      <footer style={{ backgroundColor: '#f8fafc', borderTop: '1px solid #e2e8f0', padding: '20px 24px', marginTop: 'auto' }}>
        <div style={{ maxWidth: '900px', margin: '0 auto', textAlign: 'center' }}>
          <p style={{ fontSize: '11.5px', color: '#94a3b8', lineHeight: 1.7, margin: 0 }}>
            <strong style={{ color: '#64748b' }}>Disclaimer:</strong> Casalytix provides data-driven comparable sales analysis and property tax appeal reports for informational purposes only. We do not guarantee that your tax appeal will be approved. Appeal outcomes depend on your county assessor's review, local market conditions, and the completeness of your submission. Casalytix is not a licensed appraisal firm and this report does not constitute a certified appraisal. Always review your report before submitting and consider consulting a licensed real estate professional or tax attorney.
          </p>
        </div>
      </footer>
    </div>
  );
}

// ===== INLINE STRIPE CHECKOUT FORM =====
function InlineCheckoutForm({ userId, address, onSuccess, onError }) {
  const stripe = useStripe();
  const elements = useElements();
  const [selectedPlan, setSelectedPlan] = useState('pack5'); // 'single' ($39) or 'pack5' ($79)
  const [processing, setProcessing] = useState(false);
  const [cardComplete, setCardComplete] = useState(false);
  const [addressComplete, setAddressComplete] = useState(false);

  const isSingle = selectedPlan === 'single';
  const amount = isSingle ? 3900 : 7900;
  const creditsToGrant = isSingle ? 1 : 5;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || !cardComplete || !addressComplete || processing) {
      onError('Please complete both card and billing address details.');
      return;
    }

    setProcessing(true);
    try {
      const addressElement = elements.getElement('address');
      const { value: addressValue } = await addressElement.getValue();

      const response = await fetch('https://paws2tdfrk.execute-api.us-east-1.amazonaws.com/prod/payment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount,
          userId,
          metadata: { userId, type: isSingle ? 'homeowner_single' : 'homeowner_5pack', creditsToGrant }
        }),
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to create payment intent: ${errorText}`);
      }
      const { clientSecret } = await response.json();

      const result = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement),
          billing_details: {
            email: userId,
            address: {
              line1: addressValue.address.line1,
              line2: addressValue.address.line2 || '',
              city: addressValue.address.city,
              state: addressValue.address.state,
              postal_code: addressValue.address.postal_code,
              country: addressValue.address.country,
            },
          },
        },
      });

      if (result.error) {
        onError(result.error.message);
      } else if (result.paymentIntent.status === 'succeeded') {
        console.log('Report unlock payment succeeded for:', userId);
        onSuccess();
      }
    } catch (err) {
      onError('Payment failed: ' + err.message);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      {/* Plan Selector inside modal */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '16px' }}>
        <div
          onClick={() => setSelectedPlan('single')}
          style={{
            border: `2px solid ${isSingle ? '#0ea5e9' : '#e2e8f0'}`,
            borderRadius: '8px',
            padding: '10px',
            cursor: 'pointer',
            background: isSingle ? '#f0f9ff' : 'white',
          }}
        >
          <div style={{ fontWeight: '700', fontSize: '14px', color: '#1e293b' }}>Single Report</div>
          <div style={{ fontWeight: '800', fontSize: '18px', color: '#0ea5e9' }}>$39</div>
          <div style={{ fontSize: '11px', color: '#64748b' }}>1 Credit</div>
        </div>
        <div
          onClick={() => setSelectedPlan('pack5')}
          style={{
            border: `2px solid ${!isSingle ? '#0ea5e9' : '#e2e8f0'}`,
            borderRadius: '8px',
            padding: '10px',
            cursor: 'pointer',
            background: !isSingle ? '#1e293b' : 'white',
            color: !isSingle ? 'white' : '#1e293b',
          }}
        >
          <div style={{ fontWeight: '700', fontSize: '14px' }}>5-Report Pack</div>
          <div style={{ fontWeight: '800', fontSize: '18px', color: '#38bdf8' }}>$79</div>
          <div style={{ fontSize: '11px', opacity: 0.8 }}>5 Credits (Save 60%)</div>
        </div>
      </div>

      <div style={{ marginBottom: '16px' }}>
        <AddressElement
          options={{
            mode: 'billing',
            fields: { line1: 'always', line2: 'auto', city: 'always', state: 'always', postal_code: 'always', country: 'always', phone: 'never' },
            validation: { line1: { required: 'always' }, city: { required: 'always' }, state: { required: 'always' }, postal_code: { required: 'always' }, country: { required: 'always' } },
          }}
          onChange={(e) => setAddressComplete(e.complete)}
        />
      </div>
      <div style={{ border: '1px solid #e0e0e0', borderRadius: '6px', padding: '12px', marginBottom: '16px' }}>
        <CardElement
          options={{
            style: {
              base: { fontSize: '16px', color: '#424770', '::placeholder': { color: '#aab7c4' }, lineHeight: '40px' },
              invalid: { color: '#9e2146' },
            },
            hidePostalCode: true,
            disableLink: true,
          }}
          onChange={(e) => setCardComplete(e.complete)}
        />
      </div>
      <button
        type="submit"
        disabled={!stripe || !cardComplete || !addressComplete || processing}
        style={{
          width: '100%',
          padding: '14px',
          backgroundColor: (!stripe || !cardComplete || !addressComplete || processing) ? '#ccc' : '#10b981',
          color: 'white',
          border: 'none',
          borderRadius: '8px',
          fontSize: '16px',
          fontWeight: '600',
          cursor: (!stripe || !cardComplete || !addressComplete || processing) ? 'not-allowed' : 'pointer',
        }}
      >
        {processing ? 'Processing...' : (isSingle ? 'Pay $39.00 — 1 Report Credit' : 'Pay $79.00 — 5 Report Credits')}
      </button>
    </form>
  );
}

export default Dashboard;